/**
 * One incident or maintenance window, with its timeline.
 *
 * Newest update first — the reader arriving mid-incident wants the latest
 * position, not the archaeology. The opening update is still at the bottom
 * where it belongs chronologically, so scrolling down reads as the story from
 * the end backwards, which is how every status page does it and how people
 * expect to read one.
 */


import { ds } from '../ds';
import { Link } from '../router';
import type { IncidentWire } from '../api';
import {
  formatDateTime,
  IMPACT_TONE,
  incidentStateTone,
  stateLabel,
  STATE_TONE
} from '../status';
import { monoPlain, RADIUS } from '../theme';
import { StatePill } from './Chrome';

import { useT } from '../i18n';
/** "6 Aug, 02:00 – 04:00" for a window. Local, like every other timestamp on
 *  the page — email is the place that spells out UTC. */
function windowText(startedAt: number, endsAt: number | null): string {
  if (!endsAt) return `From ${formatDateTime(startedAt)}`;
  const sameDay =
    new Date(startedAt * 1000).toDateString() === new Date(endsAt * 1000).toDateString();
  return sameDay
    ? `${formatDateTime(startedAt)} – ${new Date(endsAt * 1000).toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit'
      })}`
    : `${formatDateTime(startedAt)} – ${formatDateTime(endsAt)}`;
}

export function IncidentCard({
  incident,
  linkTitle = true
}: {
  incident: IncidentWire;
  linkTitle?: boolean;
}) {
  const { Icon } = ds();
  const t = useT();
  const isMaintenance = incident.kind === 'maintenance';
  const tone = isMaintenance ? STATE_TONE.maintenance : IMPACT_TONE[incident.impact] ?? STATE_TONE.down;

  const heading = (
    <span
      style={{
        fontFamily: "var(--font-display), 'Baloo 2', system-ui",
        fontWeight: 800,
        fontSize: 18,
        color: 'var(--text-strong)',
        letterSpacing: '-.01em'
      }}
    >
      {incident.title}
    </span>
  );

  return (
    <article
      style={{
        borderLeft: `3px solid ${tone.fill}`,
        paddingLeft: 16,
        display: 'grid',
        gap: 10
      }}
    >
      <div style={{ display: 'grid', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <StatePill tone={incidentStateTone(incident.state)} size="sm" />
          {linkTitle ? <Link to={`/incidents/${incident.id}`}>{heading}</Link> : heading}
        </div>

        <div style={{ ...monoPlain, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <Icon name={isMaintenance ? 'calendar' : 'clock'} size={12} />
            {isMaintenance
              ? windowText(incident.startedAt, incident.endsAt)
              : formatDateTime(incident.startedAt)}
          </span>
          {incident.services.length > 0 && (
            <span>· {incident.services.map((s) => s.name).join(', ')}</span>
          )}
          {!isMaintenance && (
            <span>
              · {tone.label} {t('incident-card.impact')}
            </span>
          )}
        </div>
      </div>

      {incident.updates.length > 0 && (
        <ol
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gap: 12,
            borderTop: '1px solid var(--border-hairline)',
            paddingTop: 12
          }}
        >
          {incident.updates.map((u) => (
            <li key={u.id} style={{ display: 'grid', gap: 3 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: incidentStateTone(u.state).ink,
                    background: incidentStateTone(u.state).soft,
                    borderRadius: RADIUS.pill,
                    padding: '1px 8px'
                  }}
                >
                  {stateLabel(u.state)}
                </span>
                <span style={monoPlain}>{formatDateTime(u.createdAt)}</span>
              </div>
              {/* Updates are written as prose with real line breaks; preserving
                  them is the difference between a paragraph and a wall. */}
              <p style={{ margin: 0, fontSize: 14.5, whiteSpace: 'pre-wrap' }}>{u.body}</p>
            </li>
          ))}
        </ol>
      )}
    </article>
  );
}
